const COUNTER_SELECTOR = '[data-announcement-counter]';
const ANNOUNCEMENT_SECTION_SELECTOR = '[data-announcement-section]';
const ANNOUNCEMENT_BAR_SELECTOR = '.announcement-bar';
const SLIDES_SELECTOR = '.announcement-bar__slides';
const ARROWS_SELECTOR = 'slideshow-arrows';

const DAYS_SELECTOR = '[data-announcement-counter-days]';
const HOURS_SELECTOR = '[data-announcement-counter-hours]';
const MINUTES_SELECTOR = '[data-announcement-counter-minutes]';
const SECONDS_SELECTOR = '[data-announcement-counter-seconds]';

/**
 * @param {number} value
 * @returns {string}
 */
function padTimeValue(value) {
  return String(Math.max(0, value)).padStart(2, '0');
}

/**
 * @param {number} totalSeconds
 * @returns {{ days: number, hours: number, minutes: number, seconds: number }}
 */
function getRemainingTimeParts(totalSeconds) {
  const seconds = Math.max(0, totalSeconds);

  return {
    days: Math.floor(seconds / 86400),
    hours: Math.floor((seconds % 86400) / 3600),
    minutes: Math.floor((seconds % 3600) / 60),
    seconds: seconds % 60,
  };
}

/**
 * @param {Element} element
 * @returns {element is HTMLElement}
 */
function isHTMLElement(element) {
  return element instanceof HTMLElement;
}

/**
 * @param {HTMLElement} counterElement
 * @param {number} remainingSeconds
 * @returns {void}
 */
function renderRemainingTime(counterElement, remainingSeconds) {
  const timeParts = getRemainingTimeParts(remainingSeconds);

  const daysElement = counterElement.querySelector(DAYS_SELECTOR);
  const hoursElement = counterElement.querySelector(HOURS_SELECTOR);
  const minutesElement = counterElement.querySelector(MINUTES_SELECTOR);
  const secondsElement = counterElement.querySelector(SECONDS_SELECTOR);

  if (daysElement) daysElement.textContent = padTimeValue(timeParts.days);
  if (hoursElement) hoursElement.textContent = padTimeValue(timeParts.hours);
  if (minutesElement) minutesElement.textContent = padTimeValue(timeParts.minutes);
  if (secondsElement) secondsElement.textContent = padTimeValue(timeParts.seconds);
}

/**
 * @param {HTMLElement} slide
 * @returns {boolean}
 */
function slideHasVisibleContent(slide) {
  if (slide.hidden) return false;

  const hasText = slide.textContent.trim().length > 0;
  const hasMediaOrAction = Boolean(slide.querySelector('img, picture, svg, video, a, button'));

  return hasText || hasMediaOrAction;
}

/**
 * @param {HTMLElement} sourceElement
 * @returns {void}
 */
function syncAnnouncementBarVisibility(sourceElement) {
  const announcementBar = sourceElement.closest(ANNOUNCEMENT_BAR_SELECTOR);

  if (!announcementBar || !isHTMLElement(announcementBar)) return;

  const announcementSection = announcementBar.closest(ANNOUNCEMENT_SECTION_SELECTOR);
  const slidesWrapper = announcementBar.querySelector(SLIDES_SELECTOR);

  if (!slidesWrapper || !isHTMLElement(slidesWrapper)) return;

  const slides = Array.from(slidesWrapper.children).filter(isHTMLElement);
  const hasVisibleSlide = slides.some(slideHasVisibleContent);

  announcementBar.hidden = !hasVisibleSlide;

  if (announcementSection && isHTMLElement(announcementSection)) {
    announcementSection.hidden = !hasVisibleSlide;
  }
}

/**
 * @param {HTMLElement} counterElement
 * @returns {HTMLElement[]}
 */
function getContentSlides(counterElement) {
  const announcementBar = counterElement.closest(ANNOUNCEMENT_BAR_SELECTOR);

  if (!announcementBar || !isHTMLElement(announcementBar)) return [];

  const slidesWrapper = announcementBar.querySelector(SLIDES_SELECTOR);

  if (!slidesWrapper || !isHTMLElement(slidesWrapper)) return [];

  return Array.from(slidesWrapper.children).filter((slide) => {
    return isHTMLElement(slide) && !slide.matches(COUNTER_SELECTOR);
  });
}

/**
 * @param {HTMLElement} counterElement
 * @returns {HTMLElement[]}
 */
function getAnnouncementSlides(counterElement) {
  const announcementBar = counterElement.closest(ANNOUNCEMENT_BAR_SELECTOR);

  if (!announcementBar || !isHTMLElement(announcementBar)) return [];

  const slidesWrapper = announcementBar.querySelector(SLIDES_SELECTOR);

  if (!slidesWrapper || !isHTMLElement(slidesWrapper)) return [];

  return Array.from(slidesWrapper.children).filter(isHTMLElement);
}

/**
 * @param {HTMLElement} counterElement
 * @param {boolean} isActive
 * @returns {void}
 */
/**
 * @param {HTMLElement} counterElement
 * @param {boolean} isActive
 * @returns {void}
 */
function syncCounterMode(counterElement, isActive) {
  const announcementBar = counterElement.closest(ANNOUNCEMENT_BAR_SELECTOR);

  if (!announcementBar || !isHTMLElement(announcementBar)) return;

  const arrows = announcementBar.querySelector(ARROWS_SELECTOR);
  const sliderComponent = announcementBar.querySelector('announcement-bar-component');

  const slides = getAnnouncementSlides(counterElement);
  const contentSlides = slides.filter((slide) => !slide.matches(COUNTER_SELECTOR));
  const firstContentSlideIndex = slides.findIndex((slide) => !slide.matches(COUNTER_SELECTOR));

  announcementBar.toggleAttribute('data-announcement-counter-active', isActive);

  if (isActive) {
    counterElement.hidden = false;
    counterElement.setAttribute('aria-hidden', 'false');

    contentSlides.forEach((slide) => {
      slide.setAttribute('aria-hidden', 'true');
    });

    if (arrows && isHTMLElement(arrows)) {
      arrows.hidden = true;
    }

    if (sliderComponent && typeof sliderComponent.suspend === 'function') {
      sliderComponent.suspend();
    }

    return;
  }

  counterElement.hidden = true;
  counterElement.setAttribute('aria-hidden', 'true');

  contentSlides.forEach((slide) => {
    slide.hidden = false;
  });

  if (firstContentSlideIndex >= 0) {
    contentSlides.forEach((slide) => {
      slide.setAttribute('aria-hidden', 'true');
    });

    const firstContentSlide = slides[firstContentSlideIndex];

    if (firstContentSlide) {
      firstContentSlide.setAttribute('aria-hidden', 'false');
    }

    if (sliderComponent && 'current' in sliderComponent) {
      sliderComponent.current = firstContentSlideIndex;
    }
  }

  if (arrows && isHTMLElement(arrows)) {
    arrows.hidden = contentSlides.length <= 1;
  }

  if (sliderComponent && contentSlides.length > 1) {
    sliderComponent.removeAttribute('paused');

    if (typeof sliderComponent.suspend === 'function') {
      sliderComponent.suspend();
    }

    if (typeof sliderComponent.play === 'function') {
      sliderComponent.play();
    }
  }
}

/** @type {WeakMap<HTMLElement, number>} */
const counterIntervals = new WeakMap();

/**
 * Detiene el intervalo de un contador y limpia su marca de inicialización.
 * El editor de tema reemplaza el nodo del bloque en cada edición: sin esto, el
 * `setInterval` del nodo viejo (ya desconectado) seguiría corriendo para siempre.
 * @param {HTMLElement} counterElement
 * @returns {void}
 */
function teardownAnnouncementCounter(counterElement) {
  const intervalId = counterIntervals.get(counterElement);

  if (intervalId !== undefined) {
    window.clearInterval(intervalId);
    counterIntervals.delete(counterElement);
  }

  delete counterElement.dataset.counterInitialized;
}

/**
 * @param {ParentNode} root
 * @returns {void}
 */
function teardownAnnouncementCounters(root) {
  root.querySelectorAll(COUNTER_SELECTOR).forEach((counterElement) => {
    if (isHTMLElement(counterElement)) teardownAnnouncementCounter(counterElement);
  });
}

/**
 * @param {HTMLElement} counterElement
 * @returns {void}
 */
function initAnnouncementCounter(counterElement) {
  if (counterElement.dataset.counterInitialized === 'true') return;

  counterElement.dataset.counterInitialized = 'true';

  const startTs = Number(counterElement.dataset.startTs || 0);
  const endTs = Number(counterElement.dataset.endTs || 0);

  let previousIsActive = null;

  const update = () => {
    const nowTs = Math.floor(Date.now() / 1000);

    const isBeforeStart = startTs > 0 && nowTs < startTs;
    const isExpired = !endTs || nowTs >= endTs;
    const isActive = !isBeforeStart && !isExpired;

    if (isActive) {
      renderRemainingTime(counterElement, endTs - nowTs);
    }

    /**
     * Solo sincronizamos el modo cuando cambia el estado:
     * - null -> false: inicialización antes de empezar
     * - false -> true: el contador empieza y debe aparecer
     * - true -> false: el contador finaliza y debe ocultarse
     *
     * Esto evita reiniciar el slider cada segundo antes de que empiece.
     */
    if (previousIsActive !== isActive) {
      syncCounterMode(counterElement, isActive);
      syncAnnouncementBarVisibility(counterElement);
      previousIsActive = isActive;
    }
  };

  update();

  counterIntervals.set(counterElement, window.setInterval(update, 1000));
}

/**
 * @param {ParentNode} [root=document]
 * @returns {void}
 */
function initAnnouncementCounters(root = document) {
  const counters = root.querySelectorAll(COUNTER_SELECTOR);

  counters.forEach((counterElement) => {
    if (!isHTMLElement(counterElement)) return;

    initAnnouncementCounter(counterElement);
  });
}

initAnnouncementCounters();

document.addEventListener('DOMContentLoaded', () => {
  initAnnouncementCounters();
});

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;

  if (!(target instanceof Element)) return;

  initAnnouncementCounters(target);
});

document.addEventListener('shopify:section:unload', (event) => {
  const target = event.target;

  if (!(target instanceof Element)) return;

  teardownAnnouncementCounters(target);
});